import React, { useState, useEffect } from 'react';
import {
    Typography, Card, Button, Space, Form, Input, DatePicker,
    Switch, InputNumber, message, Alert, Divider, Tag, Row, Col,
    Modal, Progress, Radio, Select, Collapse, Tooltip, Spin
} from 'antd';
import {
    SaveOutlined, PlusOutlined, DeleteOutlined, CheckCircleFilled,
    LoadingOutlined, RobotOutlined, SendOutlined, FolderOutlined,
    BookOutlined, FileTextOutlined, CaretRightOutlined, StopOutlined, ArrowLeftOutlined, LineChartOutlined, BellOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { TrainingType, AINestedSubject, AINestedChapter, AINestedModule } from '../types/training';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { cpp } from '@codemirror/lang-cpp';
import { go } from '@codemirror/lang-go';
import { java } from '@codemirror/lang-java';
import { sql } from '@codemirror/lang-sql';
import { oneDark } from '@codemirror/theme-one-dark';
import { csharp } from '@replit/codemirror-lang-csharp';
import { EditorView } from '@codemirror/view';
import dayjs from 'dayjs';
import {
    adminGetTrainingById, adminUpdateTraining, adminGenerateAIQuestions,
    adminToggleTrainingStatus,
    adminDeleteTraining, adminGenerateChapterModules,
    adminSummarizeChanges,
    adminSendProgramUpdateNotification
} from '../services/adminService';
import {
    createMoodleQuiz, getMoodleQuizQuestions, updateMoodleQuizQuestions, publishMoodleProgram, deleteMoodleQuiz
} from '../services/moodleService';
import { validateBlobSafeName } from '../utils/moduleUtils';
import { detectCommonChanges } from '../utils/changeDetection';
import { buildStoredHierarchy, findUnlinkedEditedModules } from '../utils/aiNestedSave';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const genId = () => Math.random().toString(36).substr(2, 9);

// Shared immutable helpers (same as Create page)
const updateSubjectFn = (subjects: AINestedSubject[], subjectId: string, updater: (s: AINestedSubject) => AINestedSubject): AINestedSubject[] =>
    subjects.map(s => s.id === subjectId ? updater(s) : s);

const updateChapterFn = (subjects: AINestedSubject[], subjectId: string, chapterId: string, updater: (c: AINestedChapter) => AINestedChapter): AINestedSubject[] =>
    updateSubjectFn(subjects, subjectId, s => ({ ...s, chapters: s.chapters.map(c => c.id === chapterId ? updater(c) : c) }));

const updateModuleFn = (subjects: AINestedSubject[], subjectId: string, chapterId: string, moduleId: string, updater: (m: AINestedModule) => AINestedModule): AINestedSubject[] =>
    updateChapterFn(subjects, subjectId, chapterId, c => ({ ...c, modules: c.modules.map(m => m.id === moduleId ? updater(m) : m) }));

const getAllModules = (subjects: AINestedSubject[]) => {
    const result: { subject: AINestedSubject; chapter: AINestedChapter; mod: AINestedModule }[] = [];
    subjects.forEach(s => s.chapters.forEach(c => c.modules.forEach(m => result.push({ subject: s, chapter: c, mod: m }))));
    return result;
};

const EditAINestedProgram: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [fetching, setFetching] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialValues, setInitialValues] = useState<any>(null);
    const [generatingChapterIds, setGeneratingChapterIds] = useState<Set<string>>(new Set());
    const [isActive, setIsActive] = useState(true);
    const [isPublished, setIsPublished] = useState(true);
    const [notifyParticipant, setNotifyParticipant] = useState(true);
    const [subjects, setSubjects] = useState<AINestedSubject[]>([]);
    // Existing modules removed in this edit - their Moodle quizzes are deleted on save.
    const [pendingDeletions, setPendingDeletions] = useState<{ name: string; quizId: number }[]>([]);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

    // Progress modal
    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [processStatus, setProcessStatus] = useState<'active' | 'success' | 'exception'>('active');

    // Review modal
    const [reviewModalVisible, setReviewModalVisible] = useState(false);
    const [reviewTarget, setReviewTarget] = useState<{ subjectId: string; chapterId: string; moduleId: string } | null>(null);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [reviewGenerating, setReviewGenerating] = useState(false);
    const [reviewExtraPrompt, setReviewExtraPrompt] = useState('');
    const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);

    const [form] = Form.useForm();

    useEffect(() => { if (id) loadData(parseInt(id)); }, [id]);

    const loadData = async (trainingId: number) => {
        try {
            setFetching(true);
            const data = await adminGetTrainingById(trainingId);
            setInitialValues(data);
            const isDraft = data.ispublished === false;
            setIsPublished(!isDraft);
            setIsActive(!!data.active || isDraft);

            const isEvergreen = !data.startDate || !data.endDate;
            form.setFieldsValue({
                trainingId: data.trainingId,
                trainingName: data.trainingName,
                isEvergreen,
                dateRange: isEvergreen ? null : [
                    data.startDate ? dayjs(data.startDate.split('T')[0]) : null,
                    data.endDate ? dayjs(data.endDate.split('T')[0]) : null
                ],
                passingPercentage: data.passingPercentage,
                estimatedEffortHours: data.estimatedEffortHours,
                trainingLink: data.trainingLink,
                audienceEmails: data.audienceEmails,
                stakeholderEmails: data.stakeholderEmails,
                notificationVersion: data.notificationVersion || '1.0',
                trainingType: data.trainingType || TrainingType.OPEN
            });

            // Restore hierarchy from JSON
            let parsed: any[] = [];
            try { parsed = JSON.parse(data.assessments || '[]'); } catch {}

            const restored: AINestedSubject[] = parsed.map((s: any) => ({
                id: s.id || genId(),
                name: s.name || '',
                chapters: (s.chapters || []).map((c: any) => ({
                    id: c.id || genId(),
                    name: c.name || '',
                    modules: (c.modules || []).map((m: any) => ({
                        id: m.id || genId(),
                        moodleId: m.moodleId || 0,
                        name: m.name || '',
                        originalName: m.name || '',
                        description: m.description || '',
                        difficulty: m.difficulty || 'Medium',
                        questionCount: m.questionCount || 5,
                        estimatedTime: m.estimatedTime || 30,
                        attemptLimit: m.attemptLimit || 0,
                        deadline: m.deadline || 0,
                        questions: [],
                        generated: true,
                        generating: false,
                        extraPrompt: m.extraPrompt || '',
                        isExisting: true,
                        questionsLoaded: false
                    } as any))
                }))
            }));
            setSubjects(restored);
        } catch {
            message.error('Failed to load program data');
            navigate('/admin');
        } finally {
            setFetching(false);
        }
    };

    // ── Hierarchy CRUD ────────────────────────────────────────────────────────

    const toPendingDeletion = (m: any) => ({ name: m.originalName || m.name, quizId: m.moodleId || 0 });

    const addSubject = () => setSubjects(prev => [...prev, { id: genId(), name: '', chapters: [{ id: genId(), name: '', modules: [makeNewModule()] }] }]);
    const removeSubject = (id: string) => {
        const s = subjects.find(s => s.id === id);
        if (s) {
            const removed = getAllModules([s]).filter((x: any) => x.mod.isExisting).map(x => toPendingDeletion(x.mod));
            if (removed.length) setPendingDeletions(prev => [...prev, ...removed]);
        }
        setSubjects(prev => prev.filter(s => s.id !== id));
    };

    const addChapter = (subjectId: string) =>
        setSubjects(prev => updateSubjectFn(prev, subjectId, s => ({ ...s, chapters: [...s.chapters, { id: genId(), name: '', modules: [makeNewModule()] }] })));
    const removeChapter = (subjectId: string, chapterId: string) => {
        const s = subjects.find(s => s.id === subjectId);
        const c = s?.chapters.find(c => c.id === chapterId);
        if (c) {
            const removed = c.modules.filter((m: any) => m.isExisting).map(toPendingDeletion);
            if (removed.length) setPendingDeletions(prev => [...prev, ...removed]);
        }
        setSubjects(prev => updateSubjectFn(prev, subjectId, s => ({ ...s, chapters: s.chapters.filter(c => c.id !== chapterId) })));
    };

    const addModuleToChapter = (subjectId: string, chapterId: string) =>
        setSubjects(prev => updateChapterFn(prev, subjectId, chapterId, c => ({ ...c, modules: [...c.modules, makeNewModule()] })));
    const removeModuleFromChapter = (subjectId: string, chapterId: string, moduleId: string) => {
        const s = subjects.find(s => s.id === subjectId);
        const c = s?.chapters.find(c => c.id === chapterId);
        const m = c?.modules.find(m => m.id === moduleId) as any;
        if (m?.isExisting) setPendingDeletions(prev => [...prev, toPendingDeletion(m)]);
        setSubjects(prev => updateChapterFn(prev, subjectId, chapterId, c => ({ ...c, modules: c.modules.filter(m => m.id !== moduleId) })));
    };

    const makeNewModule = (): AINestedModule => ({
        id: genId(), name: '', description: '', difficulty: 'Medium',
        questionCount: 5, estimatedTime: 30, attemptLimit: 0, deadline: 0,
        questions: [], generated: false, generating: false
    });

    const updateModuleField = (subjectId: string, chapterId: string, moduleId: string, field: keyof AINestedModule, value: any) =>
        setSubjects(prev => updateModuleFn(prev, subjectId, chapterId, moduleId, m => ({ ...m, [field]: value })));
    const updateSubjectName = (id: string, name: string) =>
        setSubjects(prev => updateSubjectFn(prev, id, s => ({ ...s, name })));
    const updateChapterName = (subjectId: string, chapterId: string, name: string) =>
        setSubjects(prev => updateChapterFn(prev, subjectId, chapterId, c => ({ ...c, name })));

    // ── AI Module Structure Generation ───────────────────────────────────────

    const handleGenerateChapterModules = async (subjectId: string, chapterId: string) => {
        const subject = subjects.find(s => s.id === subjectId);
        const chapter = subject?.chapters.find(c => c.id === chapterId);
        if (!chapter?.name.trim()) { message.warning('Enter a Chapter name before generating modules.'); return; }
        const programName = form.getFieldValue('trainingName') || 'Training Program';
        setGeneratingChapterIds(prev => new Set(prev).add(chapterId));
        try {
            const generated = await adminGenerateChapterModules(programName, subject?.name || '', chapter.name);
            if (!generated.length) throw new Error('No modules returned');
            const newModules: AINestedModule[] = generated.map(g => ({ ...makeNewModule(), name: g.name, description: g.description }));
            setSubjects(prev => updateChapterFn(prev, subjectId, chapterId, c => ({
                ...c,
                modules: c.modules.length === 1 && !c.modules[0].name.trim()
                    ? newModules
                    : [...c.modules, ...newModules]
            })));
            message.success(`Generated ${newModules.length} modules for "${chapter.name}"`);
        } catch (err: any) {
            message.error(err.message || 'Failed to generate modules');
        } finally {
            setGeneratingChapterIds(prev => { const s = new Set(prev); s.delete(chapterId); return s; });
        }
    };

    // ── AI Generation ─────────────────────────────────────────────────────────

    const handleGenerateForModule = async (subjectId: string, chapterId: string, mod: AINestedModule) => {
        const programName = form.getFieldValue('trainingName') || 'Training Program';
        if (!mod.name || !mod.description) { message.warning('Provide name and description first.'); return; }
        setSubjects(prev => updateModuleFn(prev, subjectId, chapterId, mod.id, m => ({ ...m, generating: true, generated: false, questions: [] })));
        try {
            const aiData = await adminGenerateAIQuestions(
                `Training: ${programName}. Module: ${mod.name}. Topic: ${mod.description}. Generate ${mod.questionCount} questions.`,
                mod.difficulty, mod.questionCount, mod.extraPrompt
            );
            const formatted = aiData.questions.slice(0, mod.questionCount).map((q: any) => ({
                id: genId(), question: q.Question || q.question || '', answer: q.Answer || q.answer || '',
                codeSnippet: q.CodeSnippet || q.codeSnippet || '',
                language: (q.RequiredLanguage || q.requiredLanguage || 'javascript').toLowerCase()
            }));
            setSubjects(prev => updateModuleFn(prev, subjectId, chapterId, mod.id, m => ({
                ...m, questions: formatted, estimatedTime: aiData.estimatedTimeMinutes || m.estimatedTime, generated: true, generating: false
            })));
            message.success(`Generated ${formatted.length} questions for "${mod.name}"`);
        } catch (err: any) {
            message.error(err.message || 'Failed to generate');
            setSubjects(prev => updateModuleFn(prev, subjectId, chapterId, mod.id, m => ({ ...m, generating: false })));
        }
    };

    const handleGenerateMoreQuestions = async () => {
        const mod = getReviewModule();
        if (!mod || !reviewTarget) return;
        const programName = form.getFieldValue('trainingName') || 'Training Program';
        const combinedExtra = [mod.extraPrompt, reviewExtraPrompt].filter(Boolean).join('. ');
        setReviewGenerating(true);
        try {
            const aiData = await adminGenerateAIQuestions(
                `Training: ${programName}. Module: ${mod.name}. Topic: ${mod.description}. Generate 1 distinct question.`,
                mod.difficulty, 1, combinedExtra
            );
            const newQuestions = aiData.questions.slice(0, 1).map((q: any) => ({
                id: genId(),
                question: q.Question || q.question || '',
                answer: q.Answer || q.answer || '',
                codeSnippet: q.CodeSnippet || q.codeSnippet || '',
                language: (q.RequiredLanguage || q.requiredLanguage || 'javascript').toLowerCase()
            }));
            setSubjects(prev => updateModuleFn(prev, reviewTarget.subjectId, reviewTarget.chapterId, reviewTarget.moduleId,
                m => ({ ...m, questions: [...m.questions, ...newQuestions], generated: true })
            ));
            if (!selectedQuestionId) setSelectedQuestionId(newQuestions[0]?.id ?? null);
            message.success(`Added ${newQuestions.length} more questions`);
        } catch (err: any) {
            message.error(err.message || 'Failed to generate questions');
        } finally {
            setReviewGenerating(false);
        }
    };

    // ── Review Modal ──────────────────────────────────────────────────────────

    const openReviewModal = async (subjectId: string, chapterId: string, moduleId: string) => {
        const s = subjects.find(s => s.id === subjectId);
        const c = s?.chapters.find(c => c.id === chapterId);
        const mod = c?.modules.find(m => m.id === moduleId) as any;
        if (!mod) return;

        if (mod.isExisting && !mod.questionsLoaded) {
            setLoadingQuestions(true);
            try {
                const { questions: raw } = await getMoodleQuizQuestions(initialValues?.id, mod.name, mod.moodleId || 0);
                const decodeEntities = (s: string) => s
                    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#039;/g, "'").replace(/&quot;/g, '"').replace(/&amp;/g, '&');
                const extractPlainQuestion = (html: string) => {
                    // Strip embedded <pre><code> block (stored separately as codeSnippet)
                    let text = html.replace(/<pre[\s\S]*?<\/pre>/gi, '');
                    text = text.replace(/<br\s*\/?>/gi, '\n');
                    text = text.replace(/<[^>]+>/g, '');
                    return text.trim();
                };
                const qs = raw.map((q: any) => {
                    let codeSnippet = '';
                    let language = 'javascript';
                    try {
                        const meta = JSON.parse(q.generalfeedback || '{}');
                        codeSnippet = decodeEntities(meta.codeSnippet || '');
                        language = meta.language || 'javascript';
                    } catch {}
                    return {
                        id: genId(), moodleId: q.id ?? 0,
                        question: extractPlainQuestion(decodeEntities(q.questiontext || q.question || '')),
                        answer: decodeEntities(q.graderinfo || q.graderInfo || q.answer || ''),
                        codeSnippet,
                        language,
                    };
                });
                setSubjects(prev => updateModuleFn(prev, subjectId, chapterId, moduleId, m => ({
                    ...m, questions: qs, generated: true, questionsLoaded: true
                } as any)));
            } catch { message.error('Failed to load questions from Moodle'); }
            finally { setLoadingQuestions(false); }
        }

        setReviewTarget({ subjectId, chapterId, moduleId });
        setReviewModalVisible(true);
        // auto-select first question
        const freshMod = c?.modules.find(m => m.id === moduleId);
        setSelectedQuestionId(freshMod?.questions[0]?.id ?? null);
    };

    const getReviewModule = (): (AINestedModule & { moodleId?: number; isExisting?: boolean; questionsLoaded?: boolean }) | null => {
        if (!reviewTarget) return null;
        const s = subjects.find(s => s.id === reviewTarget.subjectId);
        const c = s?.chapters.find(c => c.id === reviewTarget.chapterId);
        return (c?.modules.find(m => m.id === reviewTarget.moduleId) ?? null) as any;
    };

    const autoHeightExt = EditorView.theme({
        '&': { height: 'auto' },
        '.cm-scroller': { overflow: 'visible' },
    });

    const getCodeMirrorExtensions = (lang: string) => {
        const langExt = (() => { switch ((lang || '').toLowerCase()) {
            case 'typescript': return javascript({ typescript: true });
            case 'python':     return python();
            case 'cpp':        return cpp();
            case 'csharp':     return csharp();
            case 'go':         return go();
            case 'java':       return java();
            case 'sql':        return sql();
            default:           return javascript({ jsx: true });
        }})();
        return [langExt, autoHeightExt];
    };

    const updateReviewQuestion = (qId: string, field: 'question' | 'answer' | 'codeSnippet' | 'language', value: string) => {
        if (!reviewTarget) return;
        setSubjects(prev => updateModuleFn(prev, reviewTarget.subjectId, reviewTarget.chapterId, reviewTarget.moduleId,
            m => ({ ...m, questions: m.questions.map(q => q.id === qId ? { ...q, [field]: value } : q) })
        ));
    };

    const removeReviewQuestion = (qId: string) => {
        if (!reviewTarget) return;
        setSubjects(prev => {
            const updated = updateModuleFn(prev, reviewTarget.subjectId, reviewTarget.chapterId, reviewTarget.moduleId,
                m => ({ ...m, questions: m.questions.filter(q => q.id !== qId) })
            );
            const s = updated.find(s => s.id === reviewTarget.subjectId);
            const c = s?.chapters.find(c => c.id === reviewTarget.chapterId);
            const qs = c?.modules.find(m => m.id === reviewTarget.moduleId)?.questions ?? [];
            setSelectedQuestionId(qs[0]?.id ?? null);
            return updated;
        });
    };

    const addReviewQuestion = () => {
        if (!reviewTarget) return;
        const newId = genId();
        setSubjects(prev => updateModuleFn(prev, reviewTarget.subjectId, reviewTarget.chapterId, reviewTarget.moduleId,
            m => ({ ...m, questions: [...m.questions, { id: newId, question: '', answer: '', codeSnippet: '', language: 'javascript' }] })
        ));
        setSelectedQuestionId(newId);
    };

    // ── Publish ───────────────────────────────────────────────────────────────

    const handlePublish = async () => {
        try {
            setLoading(true);
            await publishMoodleProgram(parseInt(id!));
            setIsPublished(true);
            message.success('Program published! Participants can now access it.');
        } catch { message.error('Failed to publish program'); }
        finally { setLoading(false); }
    };

    // ── Save ──────────────────────────────────────────────────────────────────

    const onFinish = async (values: any) => {
        try {
            const allMods = getAllModules(subjects);
            if (allMods.length === 0) throw new Error('Add at least one module.');
            if (subjects.some(s => !s.name.trim())) throw new Error('All subjects must have a name.');
            if (subjects.some(s => s.chapters.some(c => !c.name.trim()))) throw new Error('All chapters must have a name.');
            if (allMods.some(({ mod }) => !mod.name.trim())) throw new Error('All modules must have a name.');
            const modNames = allMods.map(({ mod }) => mod.name.trim().toLowerCase());
            const dupMod = modNames.find((n, i) => modNames.indexOf(n) !== i);
            if (dupMod) throw new Error(`Module names must be unique. Duplicate found: "${allMods.find(({ mod }) => mod.name.trim().toLowerCase() === dupMod)?.mod.name}"`);

            const newMods = allMods.filter(({ mod }) => !(mod as any).isExisting);
            const ungenerated = newMods.filter(({ mod }) => !mod.generated || mod.questions.length === 0);
            if (ungenerated.length > 0) throw new Error(`Generate questions for new modules: ${ungenerated.map(u => u.mod.name).join(', ')}`);
            const missingDeadline = allMods.filter(({ mod }) => !((mod as any).deadline) || (mod as any).deadline <= 0);
            if (missingDeadline.length > 0) throw new Error(`Set a submission deadline for: ${missingDeadline.map(u => u.mod.name).join(', ')}`);
            // A module without a stored quiz id can't have its questions saved - say so
            // instead of silently dropping them (re-adding the module only made duplicates).
            const unlinked = findUnlinkedEditedModules(subjects);
            if (unlinked.length > 0) throw new Error(`Questions can't be saved for: ${unlinked.map(m => m.name).join(', ')} - this module isn't linked to its quiz. Nothing was saved. Please contact an admin to repair the module link.`);

            const existingModsWithMoodle = allMods.filter(({ mod }) => (mod as any).isExisting && ((mod as any).moodleId > 0));
            const totalWork = pendingDeletions.length + newMods.length + (existingModsWithMoodle.length > 0 ? 1 : 0) + 1;
            setTotalSteps(totalWork);
            setCurrentStep(0);
            setProcessStatus('active');
            setProgressMessage('Updating program...');
            setProgressModalVisible(true);
            setLoading(true);

            const cleanEmails = (s: string) => s ? s.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean).join(';') : '';
            const trainingId = values.trainingId;

            const newStartDate = values.isEvergreen ? '' : (values.dateRange?.[0] ? values.dateRange[0].format('YYYY-MM-DD') : '');
            const newEndDate = values.isEvergreen ? '' : (values.dateRange?.[1] ? values.dateRange[1].format('YYYY-MM-DD') : '');

            const changeDescriptions = detectCommonChanges(initialValues, {
                trainingName: values.trainingName,
                startDate: newStartDate,
                endDate: newEndDate,
                isEvergreen: values.isEvergreen,
                passingPercentage: values.passingPercentage,
                estimatedEffortHours: values.estimatedEffortHours,
                trainingType: values.trainingType,
                trainingLink: values.trainingLink || ''
            });
            const newModNames = newMods.map(({ mod }) => mod.name.trim());
            const nameList = (names: string[]) => names.length <= 3 ? names.join(', ') : `${names.length} modules`;
            if (newModNames.length > 0) changeDescriptions.push({ field: 'Modules Added', value: nameList(newModNames) });
            if (pendingDeletions.length > 0) changeDescriptions.push({ field: 'Modules Removed', value: nameList(pendingDeletions.map(d => d.name)) });

            // Detect subject / chapter / module renames by matching IDs against the saved hierarchy
            try {
                const original: any[] = JSON.parse(initialValues?.assessments || '[]');
                const origSubjects = new Map<string, string>();
                const origChapters = new Map<string, string>();
                const origModules  = new Map<string, string>();
                for (const s of original) {
                    if (s.id) origSubjects.set(s.id, s.name || '');
                    for (const c of s.chapters || []) {
                        if (c.id) origChapters.set(c.id, c.name || '');
                        for (const m of c.modules || []) {
                            if (m.id) origModules.set(m.id, m.name || '');
                        }
                    }
                }
                for (const s of subjects) {
                    const origS = origSubjects.get(s.id);
                    if (origS !== undefined && origS.trim() !== s.name.trim() && s.name.trim())
                        changeDescriptions.push({ field: 'Subject Renamed', value: `"${origS}" → "${s.name.trim()}"` });
                    for (const c of s.chapters) {
                        const origC = origChapters.get(c.id);
                        if (origC !== undefined && origC.trim() !== c.name.trim() && c.name.trim())
                            changeDescriptions.push({ field: 'Chapter Renamed', value: `"${origC}" → "${c.name.trim()}"` });
                        for (const m of c.modules) {
                            const origM = origModules.get(m.id);
                            if (origM !== undefined && origM.trim() !== m.name.trim() && m.name.trim())
                                changeDescriptions.push({ field: 'Module Renamed', value: `"${origM}" → "${m.name.trim()}"` });
                        }
                    }
                }
            } catch { /* ignore parse errors on malformed assessments */ }

            const programUpdate = {
                trainingId,
                trainingName: values.trainingName,
                active: isActive,
                startDate: newStartDate,
                endDate: newEndDate,
                passingPercentage: values.passingPercentage,
                estimatedEffortHours: values.estimatedEffortHours,
                trainingLink: values.trainingLink || '',
                audienceEmails: values.trainingType === TrainingType.OPEN ? '' : cleanEmails(values.audienceEmails),
                stakeholderEmails: values.trainingType === TrainingType.OPEN ? '' : cleanEmails(values.stakeholderEmails),
                notificationVersion: values.notificationVersion || '1.0',
                trainingType: values.trainingType,
                programType: 'AINested',
            };
            await adminUpdateTraining(parseInt(id!), { ...programUpdate, assessments: JSON.stringify(buildStoredHierarchy(subjects)) });
            setCurrentStep(1);

            // Delete removed modules
            if (pendingDeletions.length > 0) {
                setProgressMessage(`Removing ${pendingDeletions.length} deleted module(s)...`);
                const courseId = initialValues?.id;
                await Promise.all(pendingDeletions.map(d => deleteMoodleQuiz(courseId, d.name, d.quizId)));
                setPendingDeletions([]);
                setCurrentStep(prev => prev + 1);
            }

            // Create quizzes for new modules, then store their quiz ids in the program
            // structure - otherwise the module loses its link to its quiz.
            const createdQuizIds = new Map<string, number>();
            if (newMods.length > 0) {
                const courseId = initialValues?.id;
                const existingCount = allMods.filter(({ mod }) => (mod as any).isExisting).length;
                for (let i = 0; i < newMods.length; i++) {
                    const { subject, chapter, mod } = newMods[i];
                    setProgressMessage(`Creating quiz: ${subject.name} › ${chapter.name} › ${mod.name}...`);
                    const quizResult = await createMoodleQuiz(
                        courseId, mod.name,
                        `${subject.name} › ${chapter.name} › ${mod.name}. ${mod.description}`,
                        existingCount + i + 1, mod.questions, mod.estimatedTime || 30, mod.attemptLimit || 0, (mod as any).deadline || 0
                    );
                    if (!quizResult?.id) throw new Error(`Could not create the quiz for module "${mod.name}".`);
                    createdQuizIds.set(mod.id, quizResult.id);
                    // Now an existing, linked module - a second Save must update it, not create another quiz.
                    setSubjects(prev => updateModuleFn(prev, subject.id, chapter.id, mod.id, m => ({
                        ...m, moodleId: quizResult.id, isExisting: true, originalName: m.name, questionsLoaded: false,
                    } as any)));
                    setCurrentStep(prev => prev + 1);
                }
                await adminUpdateTraining(parseInt(id!), { ...programUpdate, assessments: JSON.stringify(buildStoredHierarchy(subjects, createdQuizIds)) });
            }

            // Sync deadline + questions (and renames) for all existing modules that have a Moodle quiz
            const existingWithMoodle = allMods.filter(({ mod }) => (mod as any).isExisting && ((mod as any).moodleId > 0));
            const failedQuestionSaves: string[] = [];
            if (existingWithMoodle.length > 0) {
                setProgressMessage(`Syncing deadlines for ${existingWithMoodle.length} module(s)...`);
                const courseId = initialValues?.id;
                for (const { subject, chapter, mod } of existingWithMoodle) {
                    const deadline = (mod as any).deadline || 0;
                    const attemptLimit = mod.attemptLimit ?? 0;
                    const timeLimit = mod.estimatedTime || 30;
                    const originalName = (mod as any).originalName || mod.name;
                    const newName = mod.name.trim() !== originalName.trim() ? mod.name.trim() : undefined;
                    if ((mod as any).questionsLoaded) {
                        const result = await updateMoodleQuizQuestions(courseId, originalName, mod.questions.map(q => ({
                            id: (q as any).moodleId ?? 0, question: q.question, answer: q.answer,
                            codeSnippet: q.codeSnippet || '', language: (q as any).language || 'javascript'
                        })), (mod as any).moodleId || 0, deadline, attemptLimit, timeLimit, newName);
                        // Moodle reports new questions it could not store - never let that pass as success.
                        if (result?.failed > 0) failedQuestionSaves.push(`${mod.name} (${result.failed})`);
                    } else {
                        await updateMoodleQuizQuestions(courseId, originalName, [], (mod as any).moodleId || 0, deadline, attemptLimit, timeLimit, newName);
                    }
                    if (newName) {
                        setSubjects(prev => updateModuleFn(prev, subject.id, chapter.id, mod.id, m => ({ ...m, originalName: newName } as any)));
                    }
                }
                setCurrentStep(prev => prev + 1);
            }

            // Legacy: rename Moodle quizzes for modules without a stored moodleId (name-based lookup)
            const renamedLegacy = allMods.filter(({ mod }) =>
                (mod as any).isExisting && !((mod as any).moodleId > 0) &&
                (mod as any).originalName && mod.name.trim() !== ((mod as any).originalName || '').trim()
            );
            for (const { subject, chapter, mod } of renamedLegacy) {
                const originalName = (mod as any).originalName!;
                const newName = mod.name.trim();
                try {
                    await updateMoodleQuizQuestions(initialValues?.id, originalName, [], 0, -1, -1, -1, newName);
                    setSubjects(prev => updateModuleFn(prev, subject.id, chapter.id, mod.id, m => ({ ...m, originalName: newName } as any)));
                } catch { /* best-effort: quiz may not exist by that name */ }
            }

            // All operations done — send notification
            if (notifyParticipant && changeDescriptions.length > 0) {
                const changeSummary = await adminSummarizeChanges(changeDescriptions);
                if (changeSummary) await adminSendProgramUpdateNotification(initialValues?.id, changeSummary, user?.name || user?.username || '');
            }

            if (failedQuestionSaves.length > 0) {
                setProcessStatus('exception');
                setProgressMessage(`Program saved, but some new questions could not be stored: ${failedQuestionSaves.join(', ')}. Please re-open these modules and add them again.`);
                setLoading(false);
                return;
            }

            setProcessStatus('success');
            setProgressMessage('Program updated successfully!');
            setTimeout(() => { setProgressModalVisible(false); setLoading(false); }, 1500);
        } catch (err: any) {
            setProcessStatus('exception');
            setProgressMessage(`Error: ${err.message || 'Failed to update'}`);
            setLoading(false);
        }
    };

    const handleCloseProgram = () => {
        Modal.confirm({
            title: 'Close this program?',
            icon: <StopOutlined style={{ color: '#ff4d4f' }} />,
            content: (
                <div>
                    <p>Closing <strong>{form.getFieldValue('trainingName')}</strong> will mark it as inactive.</p>
                    <p>Once closed, participants will see it as "Closed" and it becomes read-only.</p>
                </div>
            ),
            okText: 'Yes, Close Program', okType: 'danger', cancelText: 'No, Keep Active',
            onOk: async () => {
                try {
                    setLoading(true);
                    await adminToggleTrainingStatus(parseInt(id!), true);
                    message.success('Program closed successfully');
                    await loadData(parseInt(id!));
                } catch { message.error('Failed to close program'); }
                finally { setLoading(false); }
            }
        });
    };

    // ── Module row renderer ───────────────────────────────────────────────────

    const renderModuleRow = (subjectId: string, chapterId: string, mod: AINestedModule & { isExisting?: boolean }, isOnlyModule: boolean) => (
        <Card key={mod.id} size="small" style={{ marginBottom: 8, borderRadius: 8, border: `1px solid ${mod.isExisting ? '#d9f7be' : '#f0f0f0'}` }} bodyStyle={{ padding: '12px 16px' }}>
            {mod.isExisting && <Tag color="success" style={{ marginBottom: 4 }}>Existing</Tag>}
            <Row gutter={12} align="middle">
                <Col flex="auto">
                    <Row gutter={8}>
                        <Col xs={24} md={6}>
                            <Input placeholder="Module name" value={mod.name}
                                onChange={e => updateModuleField(subjectId, chapterId, mod.id, 'name', e.target.value)}
                                size="small"
                                prefix={<FileTextOutlined style={{ color: '#8c8c8c' }} />} />
                        </Col>
                        <Col xs={24} md={6}>
                            <Input placeholder="Topic focus" value={mod.description}
                                onChange={e => updateModuleField(subjectId, chapterId, mod.id, 'description', e.target.value)}
                                size="small" />
                            {!mod.isExisting && (
                                <Input placeholder="Extra prompt (optional)" value={mod.extraPrompt}
                                    onChange={e => updateModuleField(subjectId, chapterId, mod.id, 'extraPrompt', e.target.value)}
                                    size="small" style={{ marginTop: 4 }} />
                            )}
                        </Col>
                        <Col xs={6} md={2}>
                            <Select value={mod.difficulty} onChange={v => updateModuleField(subjectId, chapterId, mod.id, 'difficulty', v)}
                                style={{ width: '100%' }} size="small" disabled={mod.isExisting}>
                                <Option value="Easy">Easy</Option>
                                <Option value="Medium">Med</Option>
                                <Option value="Hard">Hard</Option>
                            </Select>
                        </Col>
                        <Col xs={4} md={2}>
                            <InputNumber min={1} max={20} value={mod.questionCount} size="small"
                                onChange={v => updateModuleField(subjectId, chapterId, mod.id, 'questionCount', v)}
                                style={{ width: '100%' }} placeholder="Qty" disabled={mod.isExisting} />
                        </Col>
                        <Col xs={4} md={2}>
                            <InputNumber min={1} max={300} value={mod.estimatedTime} size="small"
                                onChange={v => updateModuleField(subjectId, chapterId, mod.id, 'estimatedTime', v || 30)}
                                style={{ width: '100%' }} placeholder="Mins" />
                        </Col>
                        <Col xs={4} md={2}>
                            <InputNumber min={0} max={99} value={mod.attemptLimit} size="small"
                                onChange={v => updateModuleField(subjectId, chapterId, mod.id, 'attemptLimit', v ?? 0)}
                                style={{ width: '100%' }} placeholder="0=∞" />
                        </Col>
                        <Col xs={24} md={4}>
                            <DatePicker
                                size="small"
                                showTime={{ format: 'HH:mm:ss', defaultValue: dayjs('23:59:59', 'HH:mm:ss') }}
                                format="D MMM YY HH:mm:ss"
                                placeholder="Select deadline"
                                value={(mod as any).deadline ? dayjs.unix((mod as any).deadline) : null}
                                onChange={val => updateModuleField(subjectId, chapterId, mod.id, 'deadline', val ? val.unix() : 0)}
                                style={{ width: '100%' }}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col>
                    <Space>
                        {mod.isExisting ? (
                            <Button size="small" icon={<RobotOutlined />} loading={loadingQuestions && reviewTarget?.moduleId === mod.id}
                                onClick={() => openReviewModal(subjectId, chapterId, mod.id)}>
                                Review / Edit Qs
                            </Button>
                        ) : (
                            <>
                                <Button size="small" type={mod.generated ? 'default' : 'primary'} icon={<RobotOutlined />}
                                    loading={mod.generating}
                                    onClick={() => handleGenerateForModule(subjectId, chapterId, mod)}
                                    disabled={getAllModules(subjects).some(x => x.mod.generating) && !mod.generating}>
                                    {mod.generating ? 'Generating' : mod.generated ? 'Regenerate' : 'Generate'}
                                </Button>
                                {mod.generated && (
                                    <>
                                        <Tag color="success" icon={<CheckCircleFilled />}>{mod.questions.length} Qs</Tag>
                                        <Button type="link" size="small" onClick={() => openReviewModal(subjectId, chapterId, mod.id)}>Review</Button>
                                    </>
                                )}
                            </>
                        )}
                        <Tooltip title={isOnlyModule ? 'At least one module required' : 'Remove module'}>
                            <Button type="text" danger size="small" icon={<DeleteOutlined />}
                                disabled={isOnlyModule || !isActive}
                                onClick={() => removeModuleFromChapter(subjectId, chapterId, mod.id)} />
                        </Tooltip>
                    </Space>
                </Col>
            </Row>
        </Card>
    );

    const percent = Math.round((currentStep / Math.max(totalSteps, 1)) * 100);
    const reviewMod = getReviewModule();

    if (fetching) return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: '16px' }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin')}>
                    Back to Admin
                </Button>
                <Button
                    icon={<LineChartOutlined />}
                    type="primary"
                    ghost
                    style={{ borderColor: '#fa541c', color: '#fa541c' }}
                    onClick={() => navigate(`/training/${encodeURIComponent(form.getFieldValue('trainingId'))}?mode=analyze`)}
                >
                    Analyze
                </Button>
            </div>
            <Form form={form} layout="vertical" onFinish={onFinish}
                initialValues={{ passingPercentage: 80, notificationVersion: '1.0', trainingType: TrainingType.OPEN }}>

                {/* ── Program Details ── */}
                <Card title="1. Program Details" style={{ marginBottom: 24, borderRadius: 12 }}>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Training ID" name="trainingId">
                                <Input disabled />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Passing %" name="passingPercentage" rules={[{ required: true }]}>
                                <InputNumber min={0} max={100} style={{ width: '100%' }} disabled={!isActive}
                                    formatter={v => v ? `${v}%` : ''} parser={v => v ? parseFloat(v.replace('%', '')) : 0 as any} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Assessment Duration (Hours)" name="estimatedEffortHours">
                                <InputNumber min={0} style={{ width: '100%' }} disabled={!isActive} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Training Program Name" name="trainingName" rules={[{ required: true }]}>
                        <Input size="large" maxLength={250} showCount disabled={!isActive} />
                    </Form.Item>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.trainingType !== cur.trainingType || prev.isEvergreen !== cur.isEvergreen}>
                                {({ getFieldValue, setFieldsValue }) => {
                                    const type = getFieldValue('trainingType');
                                    const evergreen = getFieldValue('isEvergreen');
                                    if (type !== TrainingType.OPEN && evergreen) setTimeout(() => setFieldsValue({ isEvergreen: false }), 0);
                                    return (
                                        <>
                                            {type === TrainingType.OPEN && (
                                                <Form.Item name="isEvergreen" valuePropName="checked" style={{ marginBottom: 16 }}>
                                                    <Switch checkedChildren="Evergreen (No Fixed Dates)" unCheckedChildren="Fixed Dates" disabled={!isActive} />
                                                </Form.Item>
                                            )}
                                            {(!evergreen || type !== TrainingType.OPEN) ? (
                                                <Form.Item label="Training Period" name="dateRange" rules={[{ required: true, message: 'Select a period' }]}>
                                                    <DatePicker.RangePicker style={{ width: '100%' }} size="large" disabled={!isActive} />
                                                </Form.Item>
                                            ) : (
                                                <Alert message="Evergreen Program" description="No fixed dates — available immediately." type="info" showIcon style={{ marginBottom: 24 }} />
                                            )}
                                        </>
                                    );
                                }}
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Resource / Documentation Link" name="trainingLink">
                                <TextArea autoSize={{ minRows: 2, maxRows: 4 }} disabled={!isActive} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider />
                    <Form.Item label="Training Accessibility Type" name="trainingType" rules={[{ required: true }]}>
                        <Radio.Group disabled={!isActive}>
                            <Space direction="vertical">
                                <Radio value={TrainingType.OPEN}><Text strong>Open for All</Text></Radio>
                                <Radio value={TrainingType.REQUESTABLE}><Text strong>Nominated + Request to Join</Text></Radio>
                                <Radio value={TrainingType.PRIVATE}><Text strong>Private (Invite Only)</Text></Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.trainingType !== cur.trainingType}>
                        {({ getFieldValue }) => {
                            if (getFieldValue('trainingType') === TrainingType.OPEN) return null;
                            return (
                                <Row gutter={[24, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Participants" name="audienceEmails" rules={[{ required: true }]}>
                                            <TextArea rows={3} disabled={!isActive} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Stakeholders" name="stakeholderEmails" rules={[{ required: true }]}>
                                            <TextArea rows={3} disabled={!isActive} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            );
                        }}
                    </Form.Item>

                </Card>

                {/* ── Hierarchy Editor ── */}
                <Card
                    title="2. Learning Hierarchy"
                    style={{ marginBottom: 24, borderRadius: 12 }}
                    extra={isActive && <Button icon={<PlusOutlined />} onClick={addSubject} type="dashed">Add Subject</Button>}
                >
                    <Alert message="Structure: Subject → Chapter → Module (each module gets an AI-graded assessment)" type="info" showIcon style={{ marginBottom: 16 }} />
{subjects.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 32 }}>
                            <Text type="secondary">No subjects yet. Click "Add Subject" to start.</Text>
                        </div>
                    )}
                    <Collapse expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        defaultActiveKey={subjects.map(s => s.id)}>
                        {subjects.map(subject => (
                            <Panel key={subject.id}
                                header={
                                    <Space onClick={e => e.stopPropagation()}>
                                        <FolderOutlined style={{ color: '#fa541c' }} />
                                        <Input placeholder="Subject name (e.g. C++)" value={subject.name}
                                            onChange={e => updateSubjectName(subject.id, e.target.value)}
                                            style={{ width: 280, fontWeight: 600 }} onClick={e => e.stopPropagation()}
                                            disabled={!isActive} />
                                        <Tag color="orange">{subject.chapters.length} chapter{subject.chapters.length !== 1 ? 's' : ''}</Tag>
                                    </Space>
                                }
                                extra={isActive && (
                                    <Tooltip title={subjects.length === 1 ? 'At least one subject required' : 'Remove subject'}>
                                        <Button type="text" danger size="small" icon={<DeleteOutlined />}
                                            disabled={subjects.length === 1}
                                            onClick={e => { e.stopPropagation(); removeSubject(subject.id); }} />
                                    </Tooltip>
                                )}
                            >
                                <Collapse expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                                    defaultActiveKey={subject.chapters.map(c => c.id)} style={{ background: '#fafafa', borderRadius: 8 }}>
                                    {subject.chapters.map(chapter => (
                                        <Panel key={chapter.id}
                                            header={
                                                <Space onClick={e => e.stopPropagation()}>
                                                    <BookOutlined style={{ color: '#1677ff' }} />
                                                    <Input placeholder="Chapter name (e.g. C++ Basic)" value={chapter.name}
                                                        onChange={e => updateChapterName(subject.id, chapter.id, e.target.value)}
                                                        style={{ width: 260 }} onClick={e => e.stopPropagation()}
                                                        disabled={!isActive} />
                                                    <Tag color="blue">{chapter.modules.length} module{chapter.modules.length !== 1 ? 's' : ''}</Tag>
                                                </Space>
                                            }
                                            extra={isActive && (
                                                <Space onClick={e => e.stopPropagation()}>
                                                    <Button size="small" icon={<RobotOutlined />}
                                                        loading={generatingChapterIds.has(chapter.id)}
                                                        onClick={e => { e.stopPropagation(); handleGenerateChapterModules(subject.id, chapter.id); }}>
                                                        Generate Modules
                                                    </Button>
                                                    <Tooltip title={subject.chapters.length === 1 ? 'At least one chapter required' : 'Remove chapter'}>
                                                        <Button type="text" danger size="small" icon={<DeleteOutlined />}
                                                            disabled={subject.chapters.length === 1}
                                                            onClick={e => { e.stopPropagation(); removeChapter(subject.id, chapter.id); }} />
                                                    </Tooltip>
                                                </Space>
                                            )}
                                        >
                                            <Row gutter={12} style={{ marginBottom: 4, paddingLeft: 8 }}>
                                                <Col flex="auto">
                                                    <Row gutter={8}>
                                                        <Col md={6}><Text type="secondary" style={{ fontSize: 11 }}>Module Name</Text></Col>
                                                        <Col md={6}><Text type="secondary" style={{ fontSize: 11 }}>Topic / Description</Text></Col>
                                                        <Col md={2}><Text type="secondary" style={{ fontSize: 11 }}>Level</Text></Col>
                                                        <Col md={2}><Text type="secondary" style={{ fontSize: 11 }}>Qty</Text></Col>
                                                        <Col md={2}><Text type="secondary" style={{ fontSize: 11 }}>Mins</Text></Col>
                                                        <Col md={2}><Text type="secondary" style={{ fontSize: 11 }}>Att.</Text></Col>
                                                        <Col md={4}><Text type="secondary" style={{ fontSize: 11 }}>Submission Deadline</Text></Col>
                                                    </Row>
                                                </Col>
                                                <Col><Text type="secondary" style={{ fontSize: 11 }}>Actions</Text></Col>
                                            </Row>
                                            {chapter.modules.map(mod => renderModuleRow(subject.id, chapter.id, mod as any, chapter.modules.length === 1))}
                                            {isActive && (
                                                <Button type="dashed" size="small" icon={<PlusOutlined />}
                                                    onClick={() => addModuleToChapter(subject.id, chapter.id)} style={{ marginTop: 4 }}>
                                                    Add Module
                                                </Button>
                                            )}
                                        </Panel>
                                    ))}
                                </Collapse>
                                {isActive && (
                                    <Button type="dashed" size="small" icon={<PlusOutlined />}
                                        onClick={() => addChapter(subject.id)} style={{ marginTop: 8 }}>
                                        Add Chapter
                                    </Button>
                                )}
                            </Panel>
                        ))}
                    </Collapse>
                </Card>

                {/* ── Actions ── */}
                <Card style={{ borderRadius: 12 }}>
                    <Row justify="space-between" align="middle">
                        <Space>
                            {(!isActive || !isPublished) && (
                                <Button danger icon={<StopOutlined />}
                                    onClick={() => setIsDeleteModalVisible(true)}>
                                    Delete Program
                                </Button>
                            )}
                        </Space>
                        <Space>
                            <Button size="large" onClick={() => navigate('/admin')} disabled={loading}>Cancel</Button>
                            {isActive && !isPublished && (
                                <Button size="large" icon={<SaveOutlined />} loading={loading}
                                    onClick={() => form.submit()}>
                                    Save as Draft
                                </Button>
                            )}
                            {isActive && !isPublished && (
                                <Button type="primary" size="large" icon={<SendOutlined />} loading={loading}
                                    onClick={handlePublish}>
                                    Publish Program
                                </Button>
                            )}
                            {isActive && (
                                <Tooltip title={notifyParticipant ? 'Participants will be notified by email on save' : 'Participants will NOT be notified on save'}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: notifyParticipant ? '#f0f5ff' : '#fafafa', borderRadius: 6, border: `1px solid ${notifyParticipant ? '#adc6ff' : '#d9d9d9'}`, cursor: 'default' }}>
                                        <BellOutlined style={{ color: notifyParticipant ? '#1890ff' : '#bfbfbf', fontSize: 14 }} />
                                        <Typography.Text style={{ fontSize: 13, color: notifyParticipant ? '#1890ff' : '#8c8c8c', whiteSpace: 'nowrap' }}>Notify Participants</Typography.Text>
                                        <Switch size="small" checked={notifyParticipant} onChange={setNotifyParticipant} />
                                    </div>
                                </Tooltip>
                            )}
                            {isActive && isPublished && (
                                <>
                                    <Button danger size="large" icon={<StopOutlined />} onClick={handleCloseProgram}>
                                        Close Program
                                    </Button>
                                    <Button type="primary" size="large" icon={<SaveOutlined />} loading={loading}
                                        disabled={!isActive} htmlType="submit">
                                        Save Changes
                                    </Button>
                                </>
                            )}
                        </Space>
                    </Row>
                </Card>
            </Form>

            {/* ── Progress Modal ── */}
            <Modal
                title={<Space>{processStatus === 'active' && <LoadingOutlined />}<span>{processStatus === 'active' ? 'Saving...' : 'Update Complete'}</span></Space>}
                open={progressModalVisible} closable={processStatus === 'exception'} maskClosable={false}
                footer={processStatus === 'exception' ? [<Button key="close" onClick={() => setProgressModalVisible(false)}>Close</Button>] : null}
            >
                <Progress type="circle" percent={percent} status={processStatus} style={{ display: 'block', textAlign: 'center', marginBottom: 16 }} />
                <Text style={{ display: 'block', textAlign: 'center' }}>{progressMessage}</Text>
            </Modal>

            {/* ── Delete Confirm Modal ── */}
            <Modal
                title={<span style={{ color: 'red' }}>Delete Program: {initialValues?.trainingName} ({initialValues?.trainingId})</span>}
                open={isDeleteModalVisible}
                onCancel={() => { setIsDeleteModalVisible(false); setDeleteConfirmationText(''); }}
                onOk={async () => {
                    try {
                        setLoading(true);
                        await adminDeleteTraining(parseInt(id!), initialValues?.trainingId);
                        message.success('Program deleted');
                        navigate('/admin');
                    } catch { message.error('Failed to delete'); }
                    finally { setLoading(false); }
                }}
                okText="Permanently Delete"
                okButtonProps={{ danger: true, disabled: deleteConfirmationText.trim() !== initialValues?.trainingName?.trim(), loading }}
                centered
            >
                <Alert
                    message="Warning: This action cannot be undone."
                    description="This will permanently delete the training program and ALL associated data from Moodle."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Text style={{ display: 'block', marginBottom: 8 }}>
                    Please type <strong>{initialValues?.trainingName}</strong> to confirm:
                </Text>
                <Input value={deleteConfirmationText} onChange={e => setDeleteConfirmationText(e.target.value)}
                    placeholder="Type program name exactly" maxLength={250} />
            </Modal>

            {/* ── Review Modal ── */}
            <Modal title={`Review Questions — ${reviewMod?.name || ''} (${reviewMod?.questions.length ?? 0} questions)`}
                open={reviewModalVisible} onCancel={() => setReviewModalVisible(false)} width="90vw"
                styles={{ body: { padding: 0 } }}
                footer={[
                    <Button key="add" icon={<PlusOutlined />} onClick={addReviewQuestion}>Add Question</Button>,
                    <Button key="gen" icon={<RobotOutlined />} loading={reviewGenerating} onClick={handleGenerateMoreQuestions}>Generate More</Button>,
                    <Button key="done" type="primary" onClick={() => setReviewModalVisible(false)}>Done</Button>
                ]}
            >
                <Row style={{ height: '72vh' }}>
                    {/* ── Left: Question List ── */}
                    <Col span={7} style={{ borderRight: '1px solid #f0f0f0', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '12px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>Additional instructions for Generate More</Text>
                            <TextArea
                                rows={2}
                                value={reviewExtraPrompt}
                                onChange={e => setReviewExtraPrompt(e.target.value)}
                                placeholder='"Focus on code snippets", "Bug-fix questions"...'
                                style={{ fontSize: 12, marginTop: 4 }}
                            />
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                            {loadingQuestions && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}
                            {!loadingQuestions && (!reviewMod || reviewMod.questions.length === 0) && (
                                <Alert message="No questions yet. Generate or add manually." type="warning" showIcon style={{ margin: 8 }} />
                            )}
                            {!loadingQuestions && reviewMod?.questions.map((q, idx) => {
                                const isSelected = selectedQuestionId === q.id;
                                return (
                                    <div
                                        key={q.id}
                                        onClick={() => setSelectedQuestionId(q.id)}
                                        style={{
                                            padding: '8px 10px',
                                            marginBottom: 4,
                                            borderRadius: 6,
                                            cursor: 'pointer',
                                            background: isSelected ? '#e6f4ff' : 'transparent',
                                            border: isSelected ? '1px solid #91caff' : '1px solid transparent',
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Text strong style={{ fontSize: 12, color: isSelected ? '#1677ff' : undefined }}>Q{idx + 1}</Text>
                                            <Button type="text" danger size="small" icon={<DeleteOutlined />}
                                                style={{ padding: '0 4px', height: 20 }}
                                                onClick={e => { e.stopPropagation(); removeReviewQuestion(q.id); }} />
                                        </div>
                                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {q.question || '(empty)'}
                                        </Text>
                                    </div>
                                );
                            })}
                        </div>
                    </Col>

                    {/* ── Right: Question Detail ── */}
                    <Col span={17} style={{ height: '100%', overflowY: 'auto', padding: '16px 20px' }}>
                        {(() => {
                            const q = reviewMod?.questions.find(q => q.id === selectedQuestionId);
                            if (!q) return (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <Text type="secondary">Select a question from the list to view and edit</Text>
                                </div>
                            );
                            return (
                                <>
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Question</Text>
                                        <TextArea
                                            autoSize={{ minRows: 3, maxRows: 8 }}
                                            value={q.question}
                                            onChange={e => updateReviewQuestion(q.id, 'question', e.target.value)}
                                        />
                                    </div>
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Reference Answer</Text>
                                        <TextArea
                                            autoSize={{ minRows: 3, maxRows: 8 }}
                                            value={q.answer}
                                            onChange={e => updateReviewQuestion(q.id, 'answer', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <Space size={8} style={{ marginBottom: 6 }}>
                                            <Text strong>Code Snippet (optional)</Text>
                                            <Select size="small" value={q.language || 'javascript'} style={{ width: 115 }}
                                                onChange={val => updateReviewQuestion(q.id, 'language', val)}>
                                                <Option value="javascript">JavaScript</Option>
                                                <Option value="typescript">TypeScript</Option>
                                                <Option value="python">Python</Option>
                                                <Option value="java">Java</Option>
                                                <Option value="cpp">C++</Option>
                                                <Option value="csharp">C#</Option>
                                                <Option value="go">Go</Option>
                                                <Option value="sql">SQL</Option>
                                            </Select>
                                        </Space>
                                        <CodeMirror
                                            value={q.codeSnippet}
                                            theme={oneDark}
                                            extensions={getCodeMirrorExtensions(q.language || 'javascript')}
                                            onChange={val => updateReviewQuestion(q.id, 'codeSnippet', val)}
                                            basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: true }}
                                        />
                                    </div>
                                </>
                            );
                        })()}
                    </Col>
                </Row>
            </Modal>
        </div>
    );
};

export default EditAINestedProgram;
